/* eslint-disable @typescript-eslint/no-explicit-any */
import { InViewComponent } from "@/components/shared/in-view-component";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
// import { ChevronDown } from "lucide-react";
import { useState } from "react";

export const UserSelectDropdown = ({
  list,
  onChange,
  selectedValue,
  children,
  branchListKey,
  branchBindValue = "branch_id",
  branchBindLabel = "branch_name",
  selectedBranchesMap,
}: {
  list: any[];
  selectedValue: any[];
  onChange: any;
  children: React.ReactNode;
  // When provided, an inline branch multi-select is shown for each newly
  // checked customer, reading its branch options from item[branchListKey].
  branchListKey?: string;
  branchBindValue?: string;
  branchBindLabel?: string;
  // Currently assigned branches for already-selected customers, keyed by
  // customer_id, so their branch list can render pre-checked (read-only).
  selectedBranchesMap?: Record<string | number, (string | number)[]>;
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [selectionList, setSelectionList] = useState<any[]>([]);
  const [branchSelections, setBranchSelections] = useState<
    Record<string, (string | number)[]>
  >({});
  const [text, setText] = useState("");

  const handleFilter = (text1: string, text2: string, searchKey: string) => {
    const temp1 = text1?.toLowerCase();
    const temp2 = text2?.toLowerCase();
    const temp3 = searchKey?.toLowerCase();
    if (searchKey === "") return true;
    return temp1.includes(temp3) || temp2.includes(temp3);
  };

  return (
    <>
      {isPopoverOpen ? (
        <span
          className="absolute left-0 top-0 w-full h-full z-99"
          onClick={() => {
            setIsPopoverOpen(false);
            setSelectionList([]);
            setBranchSelections({});
            setText("");
          }}
        ></span>
      ) : null}

      <div tabIndex={0} className="relative">
        <div
          onClick={() => {
            if (isPopoverOpen) {
              setSelectionList([]);
              setBranchSelections({});
              setText("");
            }
            setIsPopoverOpen((prev) => !prev);
          }}
        >
          {children}
        </div>

        {isPopoverOpen ? (
          <div className="absolute w-[400px] mt-1 top-10  right-0 bg-white z-10  border border-slate-200 rounded-md shadow]">
            <div className="flex items-center gap-2 justify-between px-2 py-2 border-b">
              <Input
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={isPopoverOpen ? "Search..." : "Add Customer"}
                type="search"
              />
            </div>
            <div className="max-h-[300px] overflow-y-auto">
              {list.filter(
                (item) =>
                  handleFilter(item.customer_name, item.customer_id, text) /* &&
                  !selectedValue.includes(item.customer_id) */,
              ).length === 0 ? (
                <div className="p-4 text-center text-sm text-gray-500">
                  No result found
                </div>
              ) : (
                <InViewComponent
                  list={list.filter(
                    (item) =>
                      handleFilter(
                        item.customer_name,
                        item.customer_id,
                        text,
                      ) /* && !selectedValue.includes(item.customer_id) */,
                  )}
                >
                  {(item, index) => {
                    const isAlreadySelected = selectedValue.includes(
                      item.customer_id,
                    );
                    const isChecked =
                      isAlreadySelected ||
                      selectionList.includes(item.customer_id);
                    return (
                      <div
                        key={index}
                        className={`text-sm px-3 py-2 border-b text-slate-500 hover:bg-slate-100 ${
                          isAlreadySelected
                            ? "opacity-50 cursor-not-allowed"
                            : "cursor-pointer"
                        }`}
                      >
                        <Checkbox
                          id={item.customer_id}
                          className="translate-y-[2px] mr-[6px]"
                          checked={isChecked}
                          disabled={isAlreadySelected}
                          onCheckedChange={() => {
                            if (isAlreadySelected) return;
                            setSelectionList((prev) => {
                              if (prev.includes(item.customer_id)) {
                                return prev.filter(
                                  (id) => id !== item.customer_id,
                                );
                              } else {
                                return [...prev, item.customer_id];
                              }
                            });
                            setBranchSelections((prev) => {
                              const next = { ...prev };
                              delete next[item.customer_id];
                              return next;
                            });
                          }}
                        />
                        <label
                          htmlFor={item.customer_id}
                          className={`inline-block w-[calc(100%-22px)] ${
                            isAlreadySelected ? "cursor-not-allowed" : ""
                          }`}
                        >
                          {item?.customer_name ? (
                            <>
                              {item?.customer_name} ({item.customer_id})
                            </>
                          ) : (
                            <>{item.customer_id}</>
                          )}
                        </label>
                        {branchListKey ? (
                          <div className="mt-1 pl-[28px] border-l ml-[6px]">
                            {(item?.[branchListKey] || []).map(
                              (branch: any) => {
                                const branchId = branch?.[branchBindValue];
                                const isBranchChecked = isAlreadySelected
                                  ? (
                                      selectedBranchesMap?.[
                                        item.customer_id
                                      ] || []
                                    ).includes(branchId)
                                  : (
                                      branchSelections[item.customer_id] || []
                                    ).includes(branchId);
                                const branchCheckboxId = `${item.customer_id}-${branchId}`;
                                return (
                                  <div
                                    key={branchId}
                                    className={`flex items-center gap-[6px] py-1 ${
                                      isAlreadySelected
                                        ? "opacity-50 cursor-not-allowed"
                                        : ""
                                    }`}
                                  >
                                    <Checkbox
                                      id={branchCheckboxId}
                                      className="translate-y-[1px]"
                                      checked={isBranchChecked}
                                      disabled={isAlreadySelected}
                                      onCheckedChange={() => {
                                        if (isAlreadySelected) return;
                                        setBranchSelections((prev) => {
                                          const current =
                                            prev[item.customer_id] || [];
                                          const next = current.includes(
                                            branchId,
                                          )
                                            ? current.filter(
                                                (id: string | number) =>
                                                  id !== branchId,
                                              )
                                            : [...current, branchId];
                                          return {
                                            ...prev,
                                            [item.customer_id]: next,
                                          };
                                        });
                                        setSelectionList((prev) =>
                                          prev.includes(item.customer_id)
                                            ? prev
                                            : [...prev, item.customer_id],
                                        );
                                      }}
                                    />
                                    <label
                                      htmlFor={branchCheckboxId}
                                      className={`text-xs ${
                                        isAlreadySelected
                                          ? "cursor-not-allowed"
                                          : "cursor-pointer"
                                      }`}
                                    >
                                      {branch?.[branchBindLabel]}
                                    </label>
                                  </div>
                                );
                              },
                            )}
                          </div>
                        ) : null}
                      </div>
                    );
                  }}
                </InViewComponent>
              )}
            </div>
            <div className="border-t bg-slate-100 p-2 flex gap-2">
              <Button
                className="py-1 h-auto w-[100px]"
                onClick={() => {
                  setText("");
                  setIsPopoverOpen(false);
                  if (branchListKey) {
                    onChange(
                      selectionList.map((id) => ({
                        customer_id: id,
                        branch_id: branchSelections[id] || [],
                      })),
                    );
                  } else {
                    onChange(selectionList);
                  }
                  setSelectionList([]);
                  setBranchSelections({});
                }}
              >
                Add
              </Button>
              <Button
                variant={"outline"}
                className="py-1 h-auto border-red-300 text-red-400 w-[100px]"
                onClick={() => {
                  setText("");
                  setIsPopoverOpen(false);
                  // onChange(selectionList);
                  setSelectionList([]);
                  setBranchSelections({});
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
};
